import { useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { FadeUp } from "@/components/Motion";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", subject: "", message: "", inquiry_type: "general",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contact.ok);
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "", inquiry_type: "general" });
    } catch { toast.error("Error"); } finally { setBusy(false); }
  };

  const Input = ({ k, type = "text", ...p }) => (
    <input
      type={type}
      value={form[k]}
      onChange={(e) => set(k, e.target.value)}
      data-testid={`contact-${k}`}
      className="bg-transparent border-b border-white/20 focus:border-gold text-cream py-3 outline-none placeholder:text-cream/40 transition-colors w-full font-body"
      {...p}
    />
  );

  return (
    <div className="pt-32 pb-32 min-h-screen" data-testid="contact-page">
      <Seo title={`${t.contact.t} — Madubau Dolomiti`} description={t.seo.contact_d} path="/contact" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <FadeUp className="lg:col-span-5">
          <div className="font-display text-[10px] tracking-[0.4em] text-gold mb-6">/ Get in touch</div>
          <h1 className="font-editorial italic font-light text-5xl lg:text-6xl text-cream mb-6 leading-tight">{t.contact.t}</h1>
          <p className="text-cream/60 font-body text-lg mb-10">{t.contact.sub}</p>
          <div className="space-y-3 text-cream/70 font-body">
            <div>Gorneggweg 4A</div>
            <div>39040 Laion, Italy</div>
            <div><a href="mailto:info@madubau.com" className="text-gold hover:text-gold-light">info@madubau.com</a></div>
            <div>
              <a
                href="https://wa.me/393793407070"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="inline-flex items-center gap-2 text-cream/70 hover:text-gold transition-colors"
              >
                <svg viewBox="0 0 32 32" className="w-5 h-5" fill="#25D366" aria-hidden="true">
                  <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.515 2.478-1.318.13-.33.245-.688.245-1.06 0-.058 0-.144-.014-.216-.101-.36-.144-.372-.816-.688-.616-.286-1.202-.5-1.876-.688zM16.147 4.5c-6.352 0-11.5 5.148-11.5 11.5 0 2.078.556 4.02 1.53 5.702L4.5 27.5l5.926-1.548A11.474 11.474 0 0 0 16.147 27.5c6.352 0 11.5-5.148 11.5-11.5s-5.148-11.5-11.5-11.5zm0 21.005c-1.75 0-3.4-.487-4.803-1.318l-.344-.202-3.567.938.958-3.481-.229-.36a9.482 9.482 0 0 1-1.462-5.082c0-5.248 4.267-9.515 9.515-9.515s9.515 4.267 9.515 9.515-4.269 9.505-9.583 9.505z"/>
                </svg>
                <span>+39 379 340 7070</span>
                <span className="font-display text-[9px] tracking-[0.3em] text-gold/70 ml-2">WHATSAPP</span>
              </a>
            </div>
          </div>
        </FadeUp>

        <FadeUp className="lg:col-span-7" delay={0.1}>
          <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
            <div className="flex gap-4">
              {["general", "b2b"].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => set("inquiry_type", v)}
                  data-testid={`inquiry-${v}`}
                  className={`font-display text-[10px] tracking-[0.3em] px-6 py-3 border transition-colors ${
                    form.inquiry_type === v ? "border-gold text-gold" : "border-white/10 text-cream/60 hover:border-white/30"
                  }`}
                >
                  {v === "general" ? t.contact.t_general : t.contact.t_b2b}
                </button>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Input k="name" required placeholder={t.contact.name + " *"} />
              <Input k="email" type="email" required placeholder={t.contact.email + " *"} />
              <Input k="phone" placeholder={t.contact.phone} />
              <Input k="company" placeholder={t.contact.company} />
            </div>
            <Input k="subject" required placeholder={t.contact.subject + " *"} />
            <textarea
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              required
              rows={6}
              placeholder={t.contact.message + " *"}
              data-testid="contact-message"
              className="bg-transparent border-b border-white/20 focus:border-gold text-cream py-3 outline-none placeholder:text-cream/40 transition-colors w-full font-body"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="contact-submit"
              className="px-10 py-4 bg-gold text-black font-display text-[11px] tracking-[0.3em] hover:bg-gold-light gold-shine"
              style={{ background: "#C9A24C" }}
            >
              → {t.contact.send}
            </button>
          </form>
        </FadeUp>
      </div>
    </div>
  );
}
